import { Accordion, AccordionGroup } from '@nick-mazuk/ui/elements/accordion'
import { Box } from '@nick-mazuk/ui/elements/box'
import { Button } from '@nick-mazuk/ui/elements/button'
import { Text } from '@nick-mazuk/ui/elements/text'
import { TextContent } from '@nick-mazuk/ui/elements/text-content'
import { YouTube } from '@nick-mazuk/ui/elements/video'
import { CtaButtons } from '@nick-mazuk/ui/marketing/components/cta-buttons'
import { HeroSimple } from '@nick-mazuk/ui/marketing/layouts/heros/simple'
import BlockContent from '@sanity/block-content-to-react'
import { Layout } from 'components/layout'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { COLOR_MAP } from 'pages/music'

import type { Music } from 'lib/sanity'
import { getAllPiecesWithSlug, getPiece, imageBuilder } from 'lib/sanity'

const Cta = ({ piece }: { piece: Music }): JSX.Element => {
    if (piece.previewURL && piece.purchaseURL) {
        return (
            <CtaButtons
                buttons={[
                    {
                        value: 'Buy music',
                        href: piece.purchaseURL,
                    },
                    {
                        value: 'Preview score',
                        href: piece.previewURL,
                    },
                ]}
            />
        )
    }
    if (piece.purchaseURL) {
        return (
            <div>
                <Button value='Buy music' href={piece.purchaseURL} color='primary' />
            </div>
        )
    }
    if (piece.previewURL) {
        return (
            <div>
                <Button value='Buy music' href={piece.previewURL} color='primary' />
            </div>
        )
    }
    return (
        <Text center color='text-gray'>
            {piece.title} is not for sale.
        </Text>
    )
}

export default function AcrossLots({ piece }: { piece: Music }): JSX.Element {
    return (
        <Layout title={piece.title}>
            <section
                style={{
                    backgroundImage: `url(${imageBuilder.image(piece.image)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <div
                    className={`absolute inset-0 ${piece.image ? 'opacity-50' : ''} ${
                        COLOR_MAP[piece.color]
                    }`}
                />
                <div className='relative z-20 -mb-32 text-white sm:-mb-40 md:-mb-48 wrapper'>
                    <HeroSimple
                        title={piece.title}
                        subtitle={`For ${piece.ensemble.toLowerCase()}`}
                    />
                    <div className='max-w-4xl mx-auto overflow-hidden rounded-lg shadow-lg'>
                        <YouTube id={piece.videos[0]} title='Across Lots' />
                    </div>
                    <div className='mt-8 text-center'>
                        <Cta piece={piece} />
                    </div>
                </div>
            </section>

            <section className='relative z-0 pt-40 bg-white sm:pt-56 md:pt-64'>
                <div className='flex flex-col max-w-2xl space-y-8 wrapper'>
                    <TextContent fullwidth>
                        <BlockContent blocks={piece.description} />
                    </TextContent>
                    {piece.details.length !== 0 && (
                        <AccordionGroup>
                            {piece.details.map((detail) => {
                                return (
                                    <Accordion title={detail.title} small key={detail.title}>
                                        <TextContent>
                                            <BlockContent blocks={detail.value} />
                                        </TextContent>
                                    </Accordion>
                                )
                            })}
                        </AccordionGroup>
                    )}
                </div>
            </section>
            {piece.videos.length > 1 && (
                <section className='mt-16 wrapper'>
                    <Text h4 as='h2' center>
                        More videos
                    </Text>
                    <div className='grid gap-8 py-8 grid-flow-rows sm:grid-cols-2'>
                        {piece.videos.map((video, index) => {
                            // eslint-disable-next-line react/jsx-no-useless-fragment -- base case
                            if (index === 0) return <></>
                            return (
                                <div className='overflow-hidden rounded-lg shadow-md' key={video}>
                                    <YouTube id={video} title={piece.title} />
                                </div>
                            )
                        })}
                    </div>
                </section>
            )}
            {(piece.purchaseURL || piece.previewURL) && (
                <section className='wrapper'>
                    <Box padding='larger' style='transparent'>
                        <div className='flex flex-col space-y-8 text-center'>
                            <Text h3 as='h2'>
                                Play {piece.title}
                            </Text>
                            <Cta piece={piece} />
                        </div>
                    </Box>
                </section>
            )}
        </Layout>
    )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
    if (typeof params === 'undefined' || typeof params.slug === 'undefined') return { props: {} }
    const piece = await getPiece(params.slug)
    return {
        props: {
            piece: piece ?? null,
        },
    }
}

export const getStaticPaths: GetStaticPaths = async () => {
    const allPieces = await getAllPiecesWithSlug()
    return {
        paths:
            allPieces?.map((piece) => ({
                params: {
                    slug: piece.slug,
                },
            })) ?? [],
        fallback: false,
    }
}
